import { ChangeDetectorRef, Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FileDefinition } from '../../models/fileDefinition';

import { SHA256 } from "crypto-js"
import { FileDefinitionService } from '../../services/FileDefinitionService';
import { FilterDefinition } from '../../models/filterDefinition';
import { TransformationDefinition } from '../../models/transformationDefinition';
import { take } from 'rxjs';
import { ConsolidationDefinition } from '../../models/consolidationDefinition';
import { AuthenticationService } from '../../services/AuthenticationService';
import { ValidationDefinition } from '../../models/validationDefinition';
import { AllocationDefinition } from '../../models/allocationDefinition';
import { FilterProtocol } from '../../models/filterProtocol';
import { AllocationProtocol } from '../../models/allocationProtocol';
import { ConsolidationRule } from '../../models/consolidationRule';
import { TransformationProtocol } from '../../models/transformationProtocol';
import { ValidationProtocol } from '../../models/validationProtocol';

@Component({ standalone:false,
  selector: 'import-definition-dialog',
  templateUrl: 'import-definition-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['import-definition-dialog.component.scss']
})
export class ImportDefinitionDialogComponent {
  public title: string= "Import Definition";
  public message: string = "";
  public confirmButtonText = "Import";
  public cancelButtonText = "Cancel";
  public definitionType="";
  public errormessage: string = "";
  public infomessage: string = "";
  public validDefinitionLoaded:boolean=false;
  public definitionObject:any;
  public matchingInputDefinitions:{inputDefinition:any, matchCount:number, missCount:number}[] = [];
  public selectedInputDefinition:any;
  public isConformingWithFileDefinition:boolean=false;
  public matchingConformingFileDefinitions:{inputDefinition:FileDefinition, matchCount:number, missCount:number}[] = [];
  public selectedConformingFileDefinition:any;

  public hide = true;

  constructor(@Inject(MAT_DIALOG_DATA) private data: any, private fdService:FileDefinitionService, private authService:AuthenticationService, private dialogRef: MatDialogRef<ImportDefinitionDialogComponent>, private cd:ChangeDetectorRef) {
    if(data){
        this.title=data.title;
        this.message = data.message || this.message;
        this.definitionType = data.definitionType;
        if (data.buttonText) {
            this.confirmButtonText = data.buttonText.ok || this.confirmButtonText;
            this.cancelButtonText = data.buttonText.cancel || this.cancelButtonText;
        }
    }
  }

  ngOnInit(){
  }
  
  fileInputChange(fileInputEvent: any) {
    this.errormessage="";
    this.infomessage="";
    const file: File = fileInputEvent.target.files[0];
    var definition:any;
    if(file) {
      file.text().then(t=>{
        try {
          var obj=JSON.parse(t);
          if(obj.type==this.definitionType){
            switch(this.definitionType){
              case "fileDefinition":
                definition = FileDefinition.mapFromDTO(obj.file);
                break;
              case "filterDefinition":
                definition = FilterDefinition.mapFromDTO(obj.file);
                break;
              case "consolidationDefinition":
                definition = ConsolidationDefinition.mapFromDTO(obj.file);
                break;
              case "transformationDefinition":
                definition = TransformationDefinition.mapFromDTO(obj.file);
                this.isConformingWithFileDefinition = definition.conformsWithFileDefinitionId>0;
                break;
              case "validationDefinition":
                definition = ValidationDefinition.mapFromDTO(obj.file);
                break;
              case "allocationDefinition":
                definition = AllocationDefinition.mapFromDTO(obj.file);
                break;
              default:
                this.errormessage="unknown definition type";
                break;
            }
            if(this.verifyHash(JSON.stringify(obj.file),obj.hash)){
              definition.id=0;
              definition.version=0;
              definition.published=false;
              this.definitionObject=definition;
              this.validDefinitionLoaded=true;
              this.infomessage="valid definition: " + definition.name;
              if(this.definitionType=="filterDefinition") this.matchFilterDefinitionWithFileDefinition(definition);
              else if(this.definitionType=="consolidationDefinition") this.matchConsolidationDefinitionWithFileDefinition(definition);
              else if(this.definitionType=="transformationDefinition") this.matchTransformationDefinitionWithInputDefinition(definition);
              else if(this.definitionType=="validationDefinition") this.matchValidationDefinitionWithFileDefinition(definition);
              else if(this.definitionType=="allocationDefinition") this.matchAllocationDefinitionWithFileDefinition(definition);
              else this.cd.detectChanges(); //for file definitions there is no matching needed, so trigger change detection here to update the UI with the loaded definition info
            }
            else{
              this.errormessage="invalid definition data";
            }
          }
          else{
            this.errormessage="incompatible definition type";
          }
        } catch (error) {
          this.errormessage="invalid definition data";
        }
        this.cd.detectChanges();
      });
    }
    else {
      this.errormessage="no data";
      this.cd.detectChanges();
    }
  }

  private verifyHash(data:any, hash:string):boolean{
    var sha256Hash = SHA256(data).toString();
    return hash==sha256Hash;
  }

  private matchFilterDefinitionWithFileDefinition(filterDefinition:FilterDefinition){
    //only superadmins can use this, so no eed to filter for customer context yet
    this.fdService.getFileDefinitions(false).pipe(take(1)).subscribe(fdArray=>{
      //foreach fd create a matching score.
      //filter definitions are working off fieldDefinitions which are referenced in filterRule.
      let result:{inputDefinition:any, matchCount:number, missCount:number}[]=[];
      let filteredfdArray = fdArray.filter(fd=>(!fd.ownedByEntityId || fd.ownedByEntityId==this.authService.customerContext!.id) &&!fd.deleted && fd.published);
      filteredfdArray.forEach(fd=>{
        if(!fd.deleted && fd.published){
          let matchCount=0;
          let missCount=0;
          filterDefinition.filterProtocols.forEach(fp=>{
            fp.filterRules.forEach(fr=>{
              var index = fd.fieldDefinitions.findIndex(f=>f.name==fr.inputFieldReference.name && f.index==fr.inputFieldReference.index);
              if(index>-1){
                matchCount++;
              }
              else{
                missCount++;
              }
            });
          });
          let fdResult={inputDefinition:fd, matchCount:matchCount, missCount:missCount};
          if(fdResult.matchCount>0) result.push(fdResult);
        }
      });
      result.sort((a,b)=>b.matchCount-a.matchCount); //descending order
      this.matchingInputDefinitions=result;
      this.cd.detectChanges();
    });
  }

  private matchConsolidationDefinitionWithFileDefinition(consolidationDefinition:ConsolidationDefinition){
    //only superadmins can use this, so no eed to filter for customer context yet
    this.fdService.getFileDefinitions(false).pipe(take(1)).subscribe(fdArray=>{
      //foreach fd create a matching score.
      //consolidation definitions are working off fieldDefinitions which are referenced in consolidationRule.
      let result:{inputDefinition:any, matchCount:number, missCount:number}[]=[];
      let filteredfdArray = fdArray.filter(fd=>(!fd.ownedByEntityId || fd.ownedByEntityId==this.authService.customerContext!.id) &&!fd.deleted && fd.published);
      filteredfdArray.forEach(fd=>{
        if(!fd.deleted && fd.published){
          let matchCount=0;
          let missCount=0;
          consolidationDefinition.consolidationRules.forEach(cr=>{
            var index = fd.fieldDefinitions.findIndex(f=>f.name==cr.inputFieldName && f.index==cr.inputFieldIndex);
            if(index>-1){
              matchCount++;
            }
            else{
              missCount++;
            }
          });
          let fdResult={inputDefinition:fd, matchCount:matchCount, missCount:missCount};
          if(fdResult.matchCount>0) result.push(fdResult);
        }
      });
      result.sort((a,b)=>b.matchCount-a.matchCount); //descending order
      this.matchingInputDefinitions=result;
      this.cd.detectChanges();
    });
  }

  private matchTransformationDefinitionWithInputDefinition(transformationDefinition:TransformationDefinition){
    //only superadmins can use this, so no need to filter for customer context yet
    if(transformationDefinition.inputDefinitionType==0){
      this.fdService.getFileDefinitions(false).pipe(take(1)).subscribe(fdArray=>{
        //foreach fd create a matching score.
        //transformation definitions are working off fieldDefinitions which are referenced in the protocol.
        let result:{inputDefinition:any, matchCount:number, missCount:number}[]=[];
        let filteredfdArray = fdArray.filter(fd=>(!fd.ownedByEntityId || fd.ownedByEntityId==this.authService.customerContext!.id) &&!fd.deleted && fd.published);
        filteredfdArray.forEach(fd=>{
          let matchCount=0;
          let missCount=0;
          transformationDefinition.transformationProtocols.forEach(tp=>{
            if(tp.inputFieldReferences){
              tp.inputFieldReferences.forEach(ifr=>{
                var index = fd.fieldDefinitions.findIndex(f=>f.name==ifr.name && f.index==ifr.index);
                if(index>-1){
                  matchCount++;
                }
                else{
                  missCount++;
                }
              });
            }
          });
          let fdResult={inputDefinition:fd, matchCount:matchCount, missCount:missCount};
          if(fdResult.matchCount>0) result.push(fdResult);
        });
        result.sort((a,b)=>b.matchCount-a.matchCount); //descending order
        this.matchingInputDefinitions=result;
        this.cd.detectChanges();
      });
    }
    else if(transformationDefinition.inputDefinitionType==1){
      this.fdService.getTransformationDefinitions(false).pipe(take(1)).subscribe(tdArray=>{
        //foreach td create a matching score.
        //transformation definitions are working off fieldDefinitions which are referenced in the protocol.
        let result:{inputDefinition:any, matchCount:number, missCount:number}[]=[];
        let filteredtdArray = tdArray.filter(td=>(!td.ownedByEntityId || td.ownedByEntityId==this.authService.customerContext!.id) &&!td.deleted && td.published);
        filteredtdArray.forEach(td=>{
          let matchCount=0;
          let missCount=0;
          transformationDefinition.transformationProtocols.forEach(tp=>{
            if(tp.inputFieldReferences){
              tp.inputFieldReferences.forEach(ifr=>{
                var index = td.fieldDefinitions.findIndex(f=>f.name==ifr.name && f.index==ifr.index);
                if(index>-1){
                  matchCount++;
                }
                else{
                  missCount++;
                }
              });
            }
          });
          let tdResult={inputDefinition:td, matchCount:matchCount, missCount:missCount};
          if(tdResult.matchCount>0) result.push(tdResult);
        });
        result.sort((a,b)=>b.matchCount-a.matchCount); //descending order
        this.matchingInputDefinitions=result;
        this.cd.detectChanges();
      });
    }
    if(transformationDefinition.conformsWithFileDefinitionId){
      this.fdService.getFileDefinitions(false).pipe(take(1)).subscribe(fdArray=>{
        //foreach fd create a matching score.
        //transformation definitions can have a conforming FD, but it should match in file type and field names/index
        let result:{inputDefinition:any, matchCount:number, missCount:number}[]=[];
        let filteredfdArray = fdArray.filter(fd=>(!fd.ownedByEntityId || fd.ownedByEntityId==this.authService.customerContext!.id) &&!fd.deleted && fd.published && fd.type==transformationDefinition.outputFileType);
        filteredfdArray.forEach(fd=>{
          let matchCount=0;
          let missCount=0;
          //here i can only check if i find a file definition with same column names
          transformationDefinition.transformationProtocols.forEach(tp=>{
              var index = fd.fieldDefinitions.findIndex(f=>f.name==tp.outputFieldName && f.index==tp.outputFieldIndex);
              if(index>-1){
                matchCount++;
              }
              else{
                missCount++;
              }
          });
          let fdResult={inputDefinition:fd, matchCount:matchCount, missCount:missCount};
          if(fdResult.matchCount>0) result.push(fdResult);
        });
        result.sort((a,b)=>b.matchCount-a.matchCount); //descending order
        this.matchingConformingFileDefinitions=result;
        this.cd.detectChanges();
      });
    }
  }

  private matchValidationDefinitionWithFileDefinition(validationDefinition:ValidationDefinition){
    //only superadmins can use this, so no eed to filter for customer context yet
    this.fdService.getFileDefinitions(false).pipe(take(1)).subscribe(fdArray=>{
      //foreach fd create a matching score.
      //validation definitions are working off fieldDefinitions which are referenced in filterRule.
      let result:{inputDefinition:any, matchCount:number, missCount:number}[]=[];
      let filteredfdArray = fdArray.filter(fd=>(!fd.ownedByEntityId || fd.ownedByEntityId==this.authService.customerContext!.id) &&!fd.deleted && fd.published);
      filteredfdArray.forEach(fd=>{
        if(!fd.deleted && fd.published){
          let matchCount=0;
          let missCount=0;
          validationDefinition.validationProtocols.forEach(vp=>{
            vp.validationRules.forEach(vr=>{
              var index = fd.fieldDefinitions.findIndex(f=>f.name==vr.inputFieldReference.name && f.index==vr.inputFieldReference.index);
              if(index>-1){
                matchCount++;
              }
              else{
                missCount++;
              }
            });
          });
          let fdResult={inputDefinition:fd, matchCount:matchCount, missCount:missCount};
          if(fdResult.matchCount>0) result.push(fdResult);
        }
      });
      result.sort((a,b)=>b.matchCount-a.matchCount); //descending order
      this.matchingInputDefinitions=result;
      this.cd.detectChanges();
    });
  }

  private matchAllocationDefinitionWithFileDefinition(allocationDefinition:AllocationDefinition){
    //only superadmins can use this, so no eed to filter for customer context yet
    this.fdService.getFileDefinitions(false).pipe(take(1)).subscribe(fdArray=>{
      //foreach fd create a matching score.
      //allocation definitions are working off multiple fieldDefinitions
      let result:{inputDefinition:any, matchCount:number, missCount:number}[]=[];
      let filteredfdArray = fdArray.filter(fd=>(!fd.ownedByEntityId || fd.ownedByEntityId==this.authService.customerContext!.id) &&!fd.deleted && fd.published);
      filteredfdArray.forEach(fd=>{
        if(!fd.deleted && fd.published){
          let matchCount=0;
          let missCount=0;
          allocationDefinition.allocationProtocols.forEach(ap=>{
            var index = fd.fieldDefinitions.findIndex(f=>f.name==ap.allocationFieldReference.name && f.index==ap.allocationFieldReference.index);
            if(index>-1){
              matchCount++;
            }
            else{
              missCount++;
            }
            var index = fd.fieldDefinitions.findIndex(f=>f.name==ap.creditFieldReference.name && f.index==ap.creditFieldReference.index);
            if(index>-1){
              matchCount++;
            }
            else{
              missCount++;
            }
            var index = fd.fieldDefinitions.findIndex(f=>f.name==ap.debitFieldReference.name && f.index==ap.debitFieldReference.index);
            if(index>-1){
              matchCount++;
            }
            else{
              missCount++;
            }
            ap.allocationRules.forEach(ar=>{
              var index1 = fd.fieldDefinitions.findIndex(f=>f.name==ar.sourceFieldReference.name && f.index==ar.sourceFieldReference.index);
              if(index1>-1){
                matchCount++;
              }
              else{
                missCount++;
              }
              var index2 = fd.fieldDefinitions.findIndex(f=>f.name==ar.destinationFieldReference.name && f.index==ar.destinationFieldReference.index);
              if(index2>-1){
                matchCount++;
              }
              else{
                missCount++;
              }
            });
          });
          let fdResult={inputDefinition:fd, matchCount:matchCount, missCount:missCount};
          if(fdResult.matchCount>0) result.push(fdResult);
        }
      });
      result.sort((a,b)=>b.matchCount-a.matchCount); //descending order
      this.matchingInputDefinitions=result;
      this.cd.detectChanges();
    });
  }

  public matchPercent(match:{inputDefinition:FileDefinition|TransformationDefinition, matchCount:number, missCount:number}){
    return match.matchCount / (match.matchCount+match.missCount);
  }

  get canImport(){
    return this.validDefinitionLoaded && (this.definitionType=='fileDefinition' || this.selectedInputDefinition);
  }

  onConfirmClick(): void {
    switch(this.definitionType){
      case "filterDefinition":
        //i need to call setFileDefinition method to initiate the definition fully
        this.definitionObject.setFileDefinition(this.selectedInputDefinition);
        this.definitionObject.filterProtocols.forEach((fp:FilterProtocol)=>{
          fp.filterRules.forEach(fr=>{
              this.fdService.getFilterRuleDefinitionById(fr.filterRuleDefinitionId).pipe(take(1)).subscribe(frd=>{
                  fr.filterRuleDefinition=frd;
              });
          })
        });
        this.definitionObject.filterProtocols.sort((a:FilterProtocol,b:FilterProtocol)=>a.sequenceNo-b.sequenceNo);
        break;
      case "allocationDefinition":
        //i need to call setFileDefinition method to initiate the definition fully
        this.definitionObject.setFileDefinition(this.selectedInputDefinition);
        this.definitionObject.allocationProtocols.sort((a:AllocationProtocol,b:AllocationProtocol)=>a.sequenceNo-b.sequenceNo);
        break;
      case "consolidationDefinition":
        //i need to call setFileDefinition method to initiate the definition fully
        this.definitionObject.setFileDefinition(this.selectedInputDefinition);
        this.definitionObject.consolidationRules.forEach((cr:ConsolidationRule)=>{
            this.fdService.getConsolidationRuleDefinitionById(cr.consolidationRuleDefinitionId).pipe(take(1)).subscribe(crd=>{
                cr.consolidationRuleDefinition=crd!;
            });
        })
        break;
      case "transformationDefinition":
        //i need to call setInputDefinition method to initiate the definition fully
        this.definitionObject.setInputDefinition(this.selectedInputDefinition);
        if(this.selectedConformingFileDefinition) this.definitionObject.conformsWithFileDefinitionId=this.selectedConformingFileDefinition.id;
        this.definitionObject.transformationProtocols.forEach((tp:TransformationProtocol)=>{
          tp.transformationRules.forEach(tr=>{
              this.fdService.getTransformationRuleDefinitionById(tr.transformationRuleDefinitionId).pipe(take(1)).subscribe(trd=>{
                  tr.transformationRuleDefinition=trd!;
              });
          })
        });
        this.definitionObject.transformationProtocols.sort((a:TransformationProtocol,b:TransformationProtocol)=>a.outputFieldIndex-b.outputFieldIndex);
        break;
      case "validationDefinition":
        //i need to call setFileDefinition method to initiate the definition fully
        this.definitionObject.setFileDefinition(this.selectedInputDefinition);
        this.definitionObject.validationProtocols.forEach((vp:ValidationProtocol)=>{
          vp.validationRules.forEach(vr=>{
              this.fdService.getValidationRuleDefinitionById(vr.validationRuleDefinitionId).pipe(take(1)).subscribe(vrd=>{
                  vr.validationRuleDefinition=vrd!;
              });
          })
        });
        this.definitionObject.validationProtocols.sort((a:ValidationProtocol,b:ValidationProtocol)=>a.sequenceNo-b.sequenceNo);
        break;
      default:
        break;
    }
    this.dialogRef.close(this.definitionObject);
  }

  onCancelClick(): void {
    this.dialogRef.close(undefined);
  }

}